import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { BookGrid } from '@/components/books/BookGrid';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { AnalyticsSocialProof } from '@/components/pricing/AnalyticsSocialProof';
import { GoogleReviews } from '@/components/reviews/GoogleReviews';
import { books, genres, getBooksByVisibility, authors, type Book } from '@/data/books';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { fetchOverrides, applyOverride, getCachedOverrides, type OverridesMap } from '@/lib/bookOverrides';

const SPOTLIGHT_PER_PAGE = 6;
const PREVIOUSLY_PER_PAGE = 6;

// Map a DB book row to the static Book shape used by BookCard / BookGrid
const mapDbBookToBook = (row: any): Book => ({
  id: `db-${row.id}`,
  title: row.title || 'Untitled',
  author: row.author_name || row.display_name || 'Independent Author',
  coverImage: row.cover_image_url || '',
  description: row.description || '',
  genre: row.genre || 'Uncategorized',
  visibility:
    row.placement === 'spotlight'
      ? 'Currently Featured'
      : row.placement === 'previously_featured'
      ? 'Previously Featured'
      : 'Library Title',
  accessLink: row.access_link || '#',
  accessType: 'External',
  dateAdded: row.created_at || new Date().toISOString(),
  readers: typeof row.views === 'number' ? row.views : 0,
  clicks: typeof row.clicks === 'number' ? row.clicks : 0,
  engagementRate: typeof row.engagement_rate === 'number' ? row.engagement_rate : Number(row.engagement_rate) || 0,
  authorBio: row.author_bio || '',
  authorCountry: row.author_country || '',
});

const Index = () => {
  const staticCurrentlyFeatured = getBooksByVisibility('Currently Featured');
  const staticPreviouslyFeatured = getBooksByVisibility('Previously Featured');

  const [dbSpotlight, setDbSpotlight] = useState<Book[]>([]);
  const [dbPreviouslyFeatured, setDbPreviouslyFeatured] = useState<Book[]>([]);
  const [overrides, setOverrides] = useState<OverridesMap>(() => getCachedOverrides());
  const [spotlightPage, setSpotlightPage] = useState(1);
  const [previouslyPage, setPreviouslyPage] = useState(1);

  // Fetch published books from DB and refresh in realtime when admin makes changes
  useEffect(() => {
    const load = async () => {
      const [{ data: published }, ovs] = await Promise.all([
        supabase
          .from('books')
          .select('id, title, genre, description, cover_image_url, access_link, created_at, user_id, placement, status, views, clicks, engagement_rate, author_name, author_bio, author_country')
          .in('placement', ['spotlight', 'previously_featured'])
          .eq('status', 'Active')
          .order('updated_at', { ascending: false }),
        fetchOverrides(),
      ]);

      setOverrides(ovs);

      if (!published || published.length === 0) {
        setDbSpotlight([]);
        setDbPreviouslyFeatured([]);
        return;
      }

      // Fetch author display names for these books
      const authorIds = Array.from(new Set(published.map((b: any) => b.user_id)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', authorIds);
      const nameMap = new Map((profiles || []).map((p: any) => [p.id, p.display_name]));

      const enriched = published.map((row: any) =>
        mapDbBookToBook({ ...row, display_name: nameMap.get(row.user_id) })
      );

      setDbSpotlight(enriched.filter(b => b.visibility === 'Currently Featured'));
      setDbPreviouslyFeatured(enriched.filter(b => b.visibility === 'Previously Featured'));
    };

    load();

    // Realtime: when admin toggles placement, refresh book list. Override changes are
    // handled by the global cache invalidator wired in App.tsx, so we just re-read it.
    const channel = supabase
      .channel('homepage-published-books')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'books' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'book_metric_overrides' }, async () => {
        setOverrides(await fetchOverrides(true));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // DB-published books appear first so authors immediately see their newly published books
  const currentlyFeatured = [...dbSpotlight, ...staticCurrentlyFeatured].map(b => applyOverride(b, overrides));
  const previouslyFeatured = [...dbPreviouslyFeatured, ...staticPreviouslyFeatured].map(b => applyOverride(b, overrides));

  const totalSpotlightPages = Math.max(1, Math.ceil(currentlyFeatured.length / SPOTLIGHT_PER_PAGE));
  const spotlightBooks = currentlyFeatured.slice(
    (spotlightPage - 1) * SPOTLIGHT_PER_PAGE,
    spotlightPage * SPOTLIGHT_PER_PAGE
  );

  const totalPreviouslyPages = Math.max(1, Math.ceil(previouslyFeatured.length / PREVIOUSLY_PER_PAGE));
  const previouslyBooks = previouslyFeatured.slice(
    (previouslyPage - 1) * PREVIOUSLY_PER_PAGE,
    previouslyPage * PREVIOUSLY_PER_PAGE
  );

  return (
    <Layout>
      {/* Hero Banner */}
      <section className="relative w-full min-h-[60vh] md:h-[50vh] lg:h-[60vh] overflow-hidden bg-background flex items-center">
        {/* Image only on tablet/desktop where the baked-in title doesn't collide with our overlay */}
        <img
          src="/images/hero-banner.png"
          alt="The Pacers Library - Books, candle, coffee cup and lamp on a dark elegant table"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Stronger overlay on mobile for headline legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30 md:via-background/40 md:to-transparent" />

        <div className="relative w-full md:absolute md:bottom-0 md:left-0 md:right-0 md:pb-8 py-12 md:py-0">
          <div className="container-library text-center">
            <h1 className="heading-display text-foreground animate-fade-in text-3xl sm:text-4xl" style={{ animationDelay: '0.2s' }}>
              Discover Books by Independent Authors Worldwide
            </h1>

            <p className="text-body text-base md:text-lg mt-4 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
              A curated digital library built for organic reader discovery and long-term visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Currently Featured / Spotlight */}
      <section className="section-padding bg-card/30">
        <div className="container-library">
          <SectionHeader
            label="Currently Featured"
            title="Spotlight Collection"
            description="Hand-selected titles receiving focused reads and visibility this season."
          />
          <BookGrid books={spotlightBooks} columns={3} />

          {totalSpotlightPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <Button
                variant="outline"
                size="sm"
                disabled={spotlightPage === 1}
                onClick={() => setSpotlightPage(p => p - 1)}
              >
                Previous
              </Button>
              {Array.from({ length: totalSpotlightPages }, (_, i) => (
                <Button
                  key={i + 1}
                  variant={spotlightPage === i + 1 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSpotlightPage(i + 1)}
                  className="min-w-[36px]"
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                disabled={spotlightPage === totalSpotlightPages}
                onClick={() => setSpotlightPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Browse by Genre */}
      <section className="section-padding">
        <div className="container-library">
          <SectionHeader
            label="Explore"
            title="Browse by Genre"
            description="Discover books across diverse literary categories."
          />
          
          <div className="flex flex-wrap gap-3">
            {genres.map((genre, index) => (
              <Link
                key={genre}
                to={`/library?genre=${encodeURIComponent(genre)}`}
                className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-sm text-sm font-medium 
                         hover:bg-primary hover:text-primary-foreground transition-all duration-300
                         animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {genre}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Previously Featured */}
      <section className="section-padding bg-card/30">
        <div className="container-library">
          <SectionHeader
            label="Archive"
            title="Previously Featured"
            description="Past selections that continue to resonate with readers."
          />
          <BookGrid books={previouslyBooks} columns={3} />

          {totalPreviouslyPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <Button
                variant="outline"
                size="sm"
                disabled={previouslyPage === 1}
                onClick={() => setPreviouslyPage(p => p - 1)}
              >
                Previous
              </Button>
              {Array.from({ length: totalPreviouslyPages }, (_, i) => (
                <Button
                  key={i + 1}
                  variant={previouslyPage === i + 1 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviouslyPage(i + 1)}
                  className="min-w-[36px]"
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                disabled={previouslyPage === totalPreviouslyPages}
                onClick={() => setPreviouslyPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Author Libraries */}
      <section className="section-padding">
        <div className="container-library">
          <SectionHeader
            label="Authors"
            title="Author Libraries"
            description="Explore curated collections from established and emerging authors."
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {authors.slice(0, 6).map((author, index) => (
              <Link
                key={author.id}
                to={`/authors/${author.id}`}
                className="group p-6 bg-card rounded-lg border border-border hover:border-primary/50 
                         transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="font-serif text-xl text-muted-foreground">
                      {author.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="heading-card text-foreground group-hover:text-primary transition-colors">
                      {author.name}
                    </h3>
                    <p className="text-small mt-1">{author.country}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {author.bookIds.length} {author.bookIds.length === 1 ? 'book' : 'books'} in library
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/authors" className="btn-outline">
              View All Authors
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Analytics & Social Proof */}
      <AnalyticsSocialProof />

    </Layout>
  );
};

export default Index;
